import { useState, useRef } from "react";
import { Upload, Link, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";
import { MediaItem, normalizeMediaItem, toMediaItem } from "@/types/media.types";
import { UnsplashAttribution } from "@/components/ui/UnsplashAttribution";
import { UnsplashPicker } from "@/components/ui/UnsplashPicker";

interface ImageUploadProps {
  /**
   * Immagine corrente. Accetta un MediaItem (cover itinerario, con attribuzione)
   * oppure un URL nudo, per i casi in cui la provenienza non si persiste
   * (loghi brand/agenzia, che hanno showUnsplashButton=false).
   */
  image: string | MediaItem | null;
  /**
   * `media` porta provenienza e attribuzione dell'immagine scelta; `url` è lo stesso
   * dato in forma piatta, per i chiamanti che salvano solo l'URL.
   * Un solo callback con due argomenti — e non due callback — per evitare due
   * update consecutivi sullo stesso stato.
   */
  onImageChange: (url: string | null, media?: MediaItem | null) => void;
  maxSizeMB?: number;
  acceptedFormats?: string[];
  disabled?: boolean;
  /** Se fornita, il file viene caricato su storage e onImageChange riceve il public URL */
  uploadFn?: (file: File) => Promise<string>;
  /** Override the height of the upload/preview container (default: "h-[350px]") */
  heightClass?: string;
  /** Mostra il messaggio di upload (default: true) */
  showUploadMessage?: boolean;
  /** Controlla la visibilità dell'input URL (controlled component) */
  showUrlInput?: boolean;
  /** Callback per cambiare la visibilità dell'input URL */
  onShowUrlInputChange?: (show: boolean) => void;
  /** Mostra il pulsante di ricerca Unsplash (default: true). Disattivarlo per loghi/immagini non stock */
  showUnsplashButton?: boolean;
}

const ImageUpload = ({ 
  image, 
  onImageChange, 
  maxSizeMB = 10,
  acceptedFormats = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif'],
  disabled = false,
  uploadFn,
  heightClass = 'h-[350px]',
  showUploadMessage = true,
  showUrlInput = false,
  onShowUrlInputChange,
  showUnsplashButton = true,
}: ImageUploadProps) => {
  const { t } = useI18n();
  const currentMedia = normalizeMediaItem(image);
  const imageUrlValue = currentMedia?.url ?? null;
  const [isDragOver, setIsDragOver] = useState(false);
  const [imageUrl, setImageUrl] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [showUnsplash, setShowUnsplash] = useState(false);

  const handleFileSelect = (files: FileList | null) => {
    if (disabled || !files || files.length === 0) return;

    const file = files[0]; // Take only the first file

    // Validate file
    if (!acceptedFormats.includes(file.type)) {
      alert(`${t('imageUpload.invalidFormat')} ${acceptedFormats.join(', ')}`);
      return;
    }

    if (file.size > maxSizeMB * 1024 * 1024) {
      alert(`${t('imageUpload.fileTooLarge')} ${maxSizeMB}MB`);
      return;
    }

    if (uploadFn) {
      // Upload su storage e ritorna il public URL
      setIsLoading(true);
      uploadFn(file)
        .then((url) => onImageChange(url, toMediaItem(url, 'storage')))
        .catch((err) => {
          console.error('Storage upload error:', err);
          alert(t('imageUpload.uploadFailed'));
        })
        .finally(() => setIsLoading(false));
      return;
    }

    // Fallback: converti in base64
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      if (result) {
        onImageChange(result, toMediaItem(result, 'storage'));
      }
    };
    reader.onerror = (e) => {
      console.error('Error reading file:', e);
    };
    reader.readAsDataURL(file);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    if (disabled) return;
    e.preventDefault();
    setIsDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  };

  const handleClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleUrlSubmit = async () => {
    if (!imageUrl.trim() || disabled) return;

    setIsLoading(true);
    try {
      // Validate URL format
      const url = new URL(imageUrl);
      
      // Check if it's a valid image URL
      const response = await fetch(url, { method: 'HEAD' });
      const contentType = response.headers.get('content-type');
      
      if (!contentType || !contentType.startsWith('image/')) {
        alert(t('imageUpload.notAnImage'));
        return;
      }

      // Set the image URL directly
      onImageChange(imageUrl, toMediaItem(imageUrl));
      setImageUrl("");
      onShowUrlInputChange?.(false);
    } catch (error) {
      console.error('Error loading image from URL:', error);
      alert(t('imageUpload.invalidUrl'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      handleUrlSubmit();
    }
  };

  const handleUrlCancel = () => {
    setImageUrl("");
    onShowUrlInputChange?.(false);
  };

  const handleSelectUnsplashPhoto = (item: MediaItem) => {
    if (disabled) return;
    // I metadati di attribuzione vanno conservati insieme all'URL: da
    // images.unsplash.com non è possibile risalire a fotografo e id foto.
    onImageChange(item.url, item);
    setShowUnsplash(false);
  };

  return (
    <div className="space-y-6">
      {/* Upload Area - Always visible with image overlay */}
      <div className={`relative ${heightClass}`}>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors h-full flex flex-col justify-center items-center ${
            disabled
              ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-50'
              : isDragOver
              ? 'border-blue-400 bg-blue-50 cursor-pointer'
              : 'border-gray-300 hover:border-gray-400 cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={handleClick}
        >
          {showUploadMessage && !imageUrlValue && (
            <>
              <Upload className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <p className="text-sm text-gray-600 mb-2">{t('imageUpload.dragAndDrop')}</p>
              <p className="text-sm text-gray-500 mb-4">
                {acceptedFormats.join(', ').toUpperCase()} {t('imageUpload.upTo')} {maxSizeMB}MB
              </p>
            </>
          )}
          
          <input
            ref={fileInputRef}
            type="file"
            accept={acceptedFormats.join(',')}
            onChange={(e) => handleFileSelect(e.target.files)}
            className="hidden"
          />
        </div>

        {/* Image Preview - Absolute positioned over upload area */}
        {imageUrlValue && (
          <div 
            className={`absolute inset-0 flex items-center justify-center bg-white bg-opacity-95 rounded-lg group ${
              disabled ? 'cursor-not-allowed' : 'cursor-pointer'
            }`}
            onClick={handleClick}
          >
            <div className="relative w-full h-full flex items-center justify-center">
              <div
                className="absolute inset-0 rounded-lg bg-cover bg-center"
                style={{ backgroundImage: `url(${imageUrlValue})` }}
              />
              
              {/* Attribuzione Unsplash sull'anteprima della cover */}
              <UnsplashAttribution item={currentMedia} />

              {/* Hover overlay with upload icon - Only show if not disabled */}
              {!disabled && (
                <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex flex-col items-center justify-center text-white">
                    <Upload className="h-8 w-8 mb-2" />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Add from URL / Search Unsplash buttons */}
      <div className="flex justify-center gap-2">
        {showUrlInput && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onShowUrlInputChange?.(true)}
            disabled={disabled}
            className="flex items-center gap-2 text-sm"
          >
            <Link className="h-4 w-4" />
            <span className="text-xs">{t('imageUpload.addFromUrl')}</span>
          </Button>
        )}
        {showUnsplashButton && !showUnsplash && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setShowUnsplash(true)}
            disabled={disabled}
            className="flex items-center gap-2 text-sm"
          >
            <Search className="h-4 w-4" />
            <span className="text-xs">{t('modals.searchUnsplash')}</span>
          </Button>
        )}
      </div>

      {/* URL Input Section */}
      {showUrlInput && (
        <div className="space-y-4 p-4 border border-gray-200 rounded-lg bg-gray-50">
          <div className="flex items-center gap-2">
            <Link className="h-4 w-4 text-gray-600" />
            <span className="font-medium text-sm text-gray-700">{t('imageUpload.addImageFromUrl')}</span>
          </div>
          <div className="flex gap-2">
            <Input
              type="url"
              placeholder={t('imageUpload.urlPlaceholder')}
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              onKeyPress={handleKeyPress}
              disabled={disabled || isLoading}
              className="flex-1"
            />
            <Button
              type="button"
              onClick={handleUrlSubmit}
              disabled={disabled || isLoading || !imageUrl.trim()}
              size="sm"
            >
              {isLoading ? t('common.loading') : t('imageUpload.add')}
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleUrlCancel}
              disabled={disabled || isLoading}
              size="sm"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Unsplash Search Section */}
      {showUnsplashButton && showUnsplash && (
        <UnsplashPicker
          disabled={disabled}
          onSelect={handleSelectUnsplashPhoto}
          onClose={() => setShowUnsplash(false)}
        />
      )}

    </div>
  );
};

export default ImageUpload;
